import {
  Flex,
  LineChart,
  formatPercentage,
  PercentageValue,
  DisplaySwitch,
  ChartSkeleton,
  FlexProps
} from '@blockanalitica/ui';
import type {
  SimpleChartData,
  SimpleChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { ChartConfig } from '@blockanalitica/ui/package/types/src/components/charts/Chart';
import { useCallback, useMemo, useState } from 'react';
import { useApi } from '@/api';
import { useTheme } from 'styled-components';
import { ChartTimeFilter } from '@/components';
import { useChartPrefillDatasetsDates, useChartPeriod } from '@/hooks';

interface HistoricAprGraphsProps extends FlexProps {
  marketAddress: string;
}

export default function HistoricAprGraphs({
  marketAddress,
  ...rest
}: HistoricAprGraphsProps) {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [data, error] = useApi<SimpleChartData[]>({
    url: `/markets/${marketAddress}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const defaultConfig: ChartConfig = useMemo(() => {
    return {
      options: {
        aspectRatio: 2.3
      }
    };
  }, []);
  const config = updateChartConfigWithPeriod(defaultConfig);

  const theme = useTheme();
  const [activeDisplayOption, setActiveDisplayOption] = useState('rates');
  const displayOptions = useMemo(
    () => [
      { key: 'rates', value: 'Rates' },
      { key: 'utilization', value: 'Utilization' }
    ],
    []
  );
  const yScaleValueFormatter = useCallback(
    (value: string | number) => `${formatPercentage(Number(value))}`,
    []
  );

  const datasets: SimpleChartDataset[] = useMemo(() => {
    if (activeDisplayOption === 'rates') {
      return [
        {
          xField: 'date',
          yField: 'supply_rate',
          label: 'Earn Rate',
          borderColor: theme.colors.primary
        },
        {
          xField: 'date',
          yField: 'borrow_rate',
          label: 'Borrow Rate',
          borderColor: theme.colors.secondary
        }
      ];
    } else {
      return [
        {
          xField: 'date',
          yField: 'utilization',
          label: 'Utilization',
          borderColor: theme.colors.primary
        }
      ];
    }
  }, [activeDisplayOption, theme.colors.primary, theme.colors.secondary]);

  const chartData = useChartPrefillDatasetsDates(data, datasets);

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <Flex flexDirection="column" gap="xs" {...rest}>
        {' '}
        <ChartSkeleton />
      </Flex>
    );
  }
  return (
    <Flex flexDirection="column" gap="xs" {...rest}>
      <DisplaySwitch
        data={displayOptions}
        activeOption={activeDisplayOption}
        onChange={setActiveDisplayOption}
      />
      <LineChart
        key={activeDisplayOption}
        data={chartData}
        datasets={datasets}
        valueFormatter={(value) => <PercentageValue value={value} />}
        yScaleValueFormatter={yScaleValueFormatter}
        timeFilter={timeFilter}
        config={config}
      />
    </Flex>
  );
}
